import { Badge, Button, Card, CardHeader, Drawer, SegmentedTabs, Table, formatDate, useToast, type Column } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { createRule, fetchLedger, fetchRoles, fetchRules, ITEM_KINDS, newRuleVersion, releaseLedger, retireRule, TRIGGERS, type HowMuch, type LedgerLine, type RuleBody, type RuleInput, type RuleVersion } from "../../api/commission";
import { downloadCsv } from "../../api/reports";
import { useServices } from "../../app/services";
import { useAppContext, type Context } from "../../app/permissions";
import { nameMap, useProfiles } from "./hooks";
import { Empty, Field, Gate, MoneyInput, PageHead, css as s, errorText, isDate, isMonth, parseMoneyInput, useScope, type Scope } from "./kit";
import { bpToPercent, minorToInput, parsePercentBp } from "./ProfilesTab";

const TRIGGER_LABEL: Record<string, string> = { pt_session_delivered: "PT session delivered", payment_collected: "Payment collected", plan_sold: "Plan sold", class_taught: "Class taught", target_hit: "Target hit" };
const LEDGER_PAGE = 200;
const LEDGER_MAX = 1000;
type Tab = "rules" | "ledger";

/** Rules and ledger are separate levers (D57); the page opens with either and shows only the tabs it may. */
export const commissionAllows = (has: Context["has"]) => has("commission.rules_view") || has("commission.ledger_view");

export function CommissionPage() {
  const [params, setParams] = useSearchParams();
  const ctx = useAppContext();
  const shown = { rules: ctx.has("commission.rules_view"), ledger: ctx.has("commission.ledger_view") };
  const asked: Tab = params.get("tab") === "ledger" ? "ledger" : "rules";
  const tab: Tab = shown[asked] || ctx.status !== "ready" ? asked : asked === "rules" ? "ledger" : "rules";
  const options = ([{ value: "rules", label: "Rules" }, { value: "ledger", label: "Ledger" }] as const).filter((o) => ctx.status !== "ready" || shown[o.value]);
  return (
    <div className={s.page}>
      <PageHead title="Commission" sub="Rules are versioned: a change adds a version and never rewrites history.">
        <Button as={Link} to="/payroll" size="sm">Payroll</Button>
        <SegmentedTabs label="Commission sections" value={tab} onChange={(v) => setParams(v === "rules" ? {} : { tab: v })} options={[...options]} />
      </PageHead>
      {tab === "rules" ? <RulesTab /> : <LedgerTab />}
    </div>
  );
}

/* ---------------------------------------------------------------- rules */

export function describeHowMuch(h: HowMuch, money: (n: number) => string): string {
  if (h.kind === "percent") return `${bpToPercent(h.bp)}%`;
  if (h.kind === "fixed") return `${money(h.amount)} each`;
  return `Slabs on ${h.metric} (${h.mode})`;
}
const whoLabel = (r: RuleBody, staff: Map<string, string>, roles?: Map<string, string>) => (r.who.kind === "everyone" ? "Everyone" : r.who.kind === "role" ? (roles?.get(r.who.roleId) ?? "A role") : (staff.get(r.who.staffId) ?? r.who.staffId));

function RulesTab() {
  const sc = useScope();
  const { client } = useServices();
  const q = useQuery({ queryKey: ["commission", "rules", ...sc.scopeKey], queryFn: () => fetchRules(client), retry: false });
  const profiles = useProfiles(sc);
  const staff = nameMap(profiles.data?.profiles);
  const rolesQ = useQuery({ queryKey: ["commission", "roles"], queryFn: () => fetchRoles(client), enabled: (q.data?.rules ?? []).some((v) => v.rule.who.kind === "role"), retry: false, staleTime: 60_000 });
  const roleNames = new Map((rolesQ.data?.roles ?? []).map((x) => [x.id, x.name] as const));
  const [open, setOpen] = useState<string | "new" | null>(null);
  const canEdit = sc.ctx.has("commission.rules_edit");

  const grouped = useMemo(() => {
    const by = new Map<string, RuleVersion[]>();
    for (const r of q.data?.rules ?? []) by.set(r.id, [...(by.get(r.id) ?? []), r]);
    return [...by.values()].map((vs) => vs.sort((a, b) => a.version - b.version));
  }, [q.data]);
  const latest = (vs: RuleVersion[]) => vs[vs.length - 1]!;

  const cols: Array<Column<RuleVersion[]>> = [
    { key: "name", header: "Rule", cell: (vs) => <span className={s.truncate} title={latest(vs).name}>{latest(vs).name}</span> },
    { key: "when", header: "When", cell: (vs) => TRIGGER_LABEL[latest(vs).rule.when] ?? latest(vs).rule.when },
    { key: "who", header: "Who", cell: (vs) => <span className={s.truncate}>{whoLabel(latest(vs).rule, staff, roleNames)}</span> },
    { key: "how", header: "How much", cell: (vs) => describeHowMuch(latest(vs).rule.howMuch, sc.money) },
    { key: "ver", header: "Version", cell: (vs) => `v${latest(vs).version}` },
    { key: "st", header: "Status", cell: (vs) => (latest(vs).effectiveTo ? <Badge>Retired {formatDate(latest(vs).effectiveTo!, sc.locale, "short")}</Badge> : <Badge tone="ok" dot>Active</Badge>) },
  ];
  const openRule = grouped.find((vs) => vs[0]!.id === open);

  return (
    <Card aria-label="Commission rules">
      <CardHeader title="Rules" action={canEdit ? <Button size="sm" variant="primary" onClick={() => setOpen("new")}>New rule</Button> : undefined} />
      <Gate query={q} what="commission rules">
        {() => grouped.length === 0 ? <Empty icon="cash" title="No commission yet" description="Add a rule to start paying commission on sales and PT sessions." action={canEdit ? <Button variant="primary" onClick={() => setOpen("new")}>New rule</Button> : undefined} /> : (
          <Table label="Commission rules" columns={cols} rows={grouped} rowKey={(vs) => vs[0]!.id} onRowClick={(vs) => setOpen(vs[0]!.id)} minWidth={640} summary={`${grouped.length} rules`} />
        )}
      </Gate>
      {open === "new" ? <RuleDrawer sc={sc} staff={staff} onClose={() => setOpen(null)} /> : null}
      {openRule ? <RuleDetail sc={sc} versions={openRule} staff={staff} roleNames={roleNames} canEdit={canEdit} onClose={() => setOpen(null)} /> : null}
    </Card>
  );
}

function RuleDetail({ sc, versions, staff, roleNames, canEdit, onClose }: { sc: Scope; versions: RuleVersion[]; staff: Map<string, string>; roleNames: Map<string, string>; canEdit: boolean; onClose: () => void }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const last = versions[versions.length - 1]!;
  const [mode, setMode] = useState<"view" | "edit" | "retire">("view");
  const [endOn, setEndOn] = useState(sc.today);
  const [err, setErr] = useState<string | null>(null);
  const retire = useMutation({
    mutationFn: () => retireRule(client, last.id, endOn),
    onSuccess: async () => { toast.show("Rule retired"); await qc.invalidateQueries({ queryKey: ["commission"] }); onClose(); },
    onError: (e) => setErr(errorText(e)),
  });
  const editable = last.rule.howMuch.kind !== "slabs";
  if (mode === "edit") return <RuleDrawer sc={sc} staff={staff} base={last} onClose={() => setMode("view")} onSaved={onClose} />;
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title={last.name} description={`Current version v${last.version}`}
      footer={canEdit && !last.effectiveTo ? (mode === "retire"
        ? <><Button onClick={() => setMode("view")}>Cancel</Button><Button variant="primary" busy={retire.isPending} busyLabel="Retiring…" disabled={!isDate(endOn)} onClick={() => retire.mutate()}>Retire rule</Button></>
        : <><Button onClick={() => setMode("retire")}>Retire</Button>{editable ? <Button variant="primary" onClick={() => setMode("edit")}>New version</Button> : null}</>) : undefined}>
      <div className={`${s.rows} ${s.drawerPad}`}>
        <ul className={s.list} aria-label="Rule details">
          <li><span>When</span><span>{TRIGGER_LABEL[last.rule.when] ?? last.rule.when}</span></li>
          <li><span>Who</span><span>{whoLabel(last.rule, staff, roleNames)}</span></li>
          <li><span>How much</span><span>{describeHowMuch(last.rule.howMuch, sc.money)}</span></li>
          {last.rule.what.itemKind ? <li><span>Item</span><span>{last.rule.what.itemKind}</span></li> : null}
          {last.rule.what.itemIds?.length ? <li><span>Limited to items</span><span>{last.rule.what.itemIds.length}</span></li> : null}
          {last.rule.what.branchIds?.length ? <li><span>Limited to branches</span><span>{last.rule.what.branchIds.length}</span></li> : null}
          {last.rule.conditions?.saleType ? <li><span>Sale type</span><span>{last.rule.conditions.saleType}</span></li> : null}
          {last.rule.conditions?.minAmount !== undefined ? <li><span>Minimum sale</span><span>{sc.money(last.rule.conditions.minAmount)}</span></li> : null}
          {last.rule.conditions?.perEventCap !== undefined ? <li><span>Cap per event</span><span>{sc.money(last.rule.conditions.perEventCap)}</span></li> : null}
          {last.rule.conditions?.monthlyCap !== undefined ? <li><span>Monthly cap</span><span>{sc.money(last.rule.conditions.monthlyCap)}</span></li> : null}
        </ul>
        {!editable ? <p className={s.note}>Slab rules are read-only here. Create a new rule to change them.</p> : null}
        {mode === "retire" ? <Field label="Retire from (last day it applies)" error={isDate(endOn) ? null : "Use a date like 2026-11-30."}>{(a) => <input {...a} className={s.input} value={endOn} onChange={(e) => setEndOn(e.target.value)} placeholder="YYYY-MM-DD" />}</Field> : null}
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
        <h3>Version history</h3>
        <ol className={s.list} aria-label="Version history">
          {[...versions].reverse().map((v) => (
            <li key={v.version}><span>v{v.version} · {describeHowMuch(v.rule.howMuch, sc.money)}</span><span>from {formatDate(v.effectiveFrom, sc.locale, "short")}{v.effectiveTo ? ` to ${formatDate(v.effectiveTo, sc.locale, "short")}` : ""}</span></li>
          ))}
        </ol>
      </div>
    </Drawer>
  );
}

function RuleDrawer({ sc, staff, base, onClose, onSaved }: { sc: Scope; staff: Map<string, string>; base?: RuleVersion; onClose: () => void; onSaved?: () => void }) {
  const { client } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const r = base?.rule;
  const [name, setName] = useState(base?.name ?? "");
  const [when, setWhen] = useState<string>(r?.when ?? "plan_sold");
  const [who, setWho] = useState<"everyone" | "role" | "staff">(r?.who.kind ?? "everyone");
  const [whoId, setWhoId] = useState(r?.who.kind === "role" ? r.who.roleId : r?.who.kind === "staff" ? r.who.staffId : "");
  const [whoTouched, setWhoTouched] = useState(false);
  const roles = useQuery({ queryKey: ["commission", "roles"], queryFn: () => fetchRoles(client), enabled: who === "role", retry: false, staleTime: 60_000 });
  const [item, setItem] = useState(r?.what.itemKind ?? "");
  const [how, setHow] = useState<"percent" | "fixed">(r?.howMuch.kind === "fixed" ? "fixed" : "percent");
  const [amount, setAmount] = useState(r?.howMuch.kind === "percent" ? bpToPercent(r.howMuch.bp) : r?.howMuch.kind === "fixed" ? minorToInput(r.howMuch.amount, sc.digits) : "");
  const [saleType, setSaleType] = useState<string>(r?.conditions?.saleType ?? "");
  const [minAmt, setMinAmt] = useState(r?.conditions?.minAmount !== undefined ? minorToInput(r.conditions.minAmount, sc.digits) : "");
  const [eventCap, setEventCap] = useState(r?.conditions?.perEventCap !== undefined ? minorToInput(r.conditions.perEventCap, sc.digits) : "");
  const [monthCap, setMonthCap] = useState(r?.conditions?.monthlyCap !== undefined ? minorToInput(r.conditions.monthlyCap, sc.digits) : "");
  const [from, setFrom] = useState(sc.today);
  const [stackable, setStackable] = useState(base?.stackable ?? false);
  const [err, setErr] = useState<string | null>(null);

  const money = (t: string) => (t.trim() === "" ? undefined : parseMoneyInput(t, sc.currency, sc.locale));
  const howValue = how === "percent" ? parsePercentBp(amount) : parseMoneyInput(amount, sc.currency, sc.locale);
  const caps = { minAmount: money(minAmt), perEventCap: money(eventCap), monthlyCap: money(monthCap) };
  const errs = {
    name: name.trim() === "" ? "Give the rule a name." : null,
    who: who !== "everyone" && whoId.trim() === "" ? "Choose who it applies to." : null,
    amount: howValue === null ? (how === "percent" ? "Enter a percent like 10 or 2.5." : "Enter an amount.") : how === "percent" && howValue > 10_000 ? "At most 100%." : null,
    caps: Object.values(caps).some((v) => v === null) ? "Enter amounts like 5000." : null,
    from: isDate(from) ? null : "Use a date like 2026-12-01.",
  };
  const bad = Object.values(errs).some(Boolean);

  const build = (): RuleInput => {
    const conditions: NonNullable<RuleBody["conditions"]> = {
      ...(saleType ? { saleType: saleType as "new" | "renewal" } : {}),
      ...(caps.minAmount != null ? { minAmount: caps.minAmount } : {}),
      ...(caps.perEventCap != null ? { perEventCap: caps.perEventCap } : {}),
      ...(caps.monthlyCap != null ? { monthlyCap: caps.monthlyCap } : {}),
    };
    return {
      name: name.trim(),
      rule: {
        when: when as RuleBody["when"],
        who: who === "everyone" ? { kind: "everyone" } : who === "role" ? { kind: "role", roleId: whoId.trim() } : { kind: "staff", staffId: whoId },
        // The form edits the item kind only; item and branch limits carry over, or a new version would widen the rule.
        what: { ...(item ? { itemKind: item } : {}), ...(r?.what.itemIds ? { itemIds: r.what.itemIds } : {}), ...(r?.what.branchIds ? { branchIds: r.what.branchIds } : {}) },
        howMuch: how === "percent" ? { kind: "percent", bp: howValue! } : { kind: "fixed", amount: howValue! },
        ...(Object.keys(conditions).length ? { conditions } : {}),
      },
      effectiveFrom: from, priority: base?.priority ?? null, stackable,
    };
  };
  const save = useMutation({
    mutationFn: () => (base ? newRuleVersion(client, base.id, build()) : createRule(client, build())),
    onSuccess: async () => { toast.show(base ? "New version saved" : "Rule created"); await qc.invalidateQueries({ queryKey: ["commission"] }); onClose(); onSaved?.(); },
    onError: (e) => setErr(errorText(e)),
  });

  return (
    <Drawer open onOpenChange={(o) => !o && onClose()} title={base ? `New version of ${base.name}` : "New commission rule"} description={base ? "Saves as the next version. Earlier versions stay as they were." : undefined}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={bad} busy={save.isPending} onClick={() => save.mutate()}>{base ? "Save new version" : "Create rule"}</Button></>}>
      <div className={`${s.rows} ${s.drawerPad}`}>
        <Field label="Name" error={name === "" ? null : errs.name}>{(a) => <input {...a} className={s.input} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label="When">{(a) => <select {...a} className={s.input} value={when} onChange={(e) => setWhen(e.target.value)}>{TRIGGERS.map((t) => <option key={t} value={t}>{TRIGGER_LABEL[t]}</option>)}</select>}</Field>
        <div className={s.grid2}>
          <Field label="Who">{(a) => <select {...a} className={s.input} value={who} onChange={(e) => { setWho(e.target.value as typeof who); setWhoId(""); setWhoTouched(false); }}><option value="everyone">Everyone</option><option value="staff">One person</option><option value="role">A role</option></select>}</Field>
          {who === "staff" ? <Field label="Person" error={whoTouched ? errs.who : null}>{(a) => <select {...a} className={s.input} value={whoId} onBlur={() => setWhoTouched(true)} onChange={(e) => { setWhoId(e.target.value); setWhoTouched(true); }}><option value="">Choose staff</option>{[...staff].map(([id, n]) => <option key={id} value={id}>{n}</option>)}</select>}</Field> : null}
          {who === "role" ? <Field label="Role" error={whoTouched ? errs.who : null} hint={roles.isError ? "You need permission to view roles to pick one" : undefined}>{(a) => {
            const list = roles.data?.roles ?? [];
            const unknown = whoId !== "" && roles.isSuccess && !list.some((x) => x.id === whoId);
            return (
              <select {...a} className={s.input} value={whoId} disabled={roles.isPending || roles.isError} onBlur={() => setWhoTouched(true)} onChange={(e) => { setWhoId(e.target.value); setWhoTouched(true); }}>
                <option value="">{roles.isPending ? "Loading roles…" : "Choose a role"}</option>
                {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                {unknown ? <option value={whoId}>Unknown role</option> : null}
                {roles.isError && whoId !== "" ? <option value={whoId}>Unknown role</option> : null}
              </select>
            );
          }}</Field> : null}
        </div>
        <Field label="Item kind" hint="Leave empty for any item">{(a) => <select {...a} className={s.input} value={item} onChange={(e) => setItem(e.target.value)}><option value="">Any</option>{ITEM_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select>}</Field>
        <div className={s.grid2}>
          <Field label="Paid as">{(a) => <select {...a} className={s.input} value={how} onChange={(e) => { setHow(e.target.value as typeof how); setAmount(""); }}><option value="percent">Percent of the sale before tax</option><option value="fixed">Fixed amount each</option></select>}</Field>
          <Field label={how === "percent" ? "Percent" : "Amount"} hint={how === "percent" ? "Worked out on the amount before GST" : undefined} error={amount === "" ? null : errs.amount}>{(a) => <MoneyInput {...a} value={amount} onChange={setAmount} />}</Field>
        </div>
        <Field label="Sale type" hint="Optional">{(a) => <select {...a} className={s.input} value={saleType} onChange={(e) => setSaleType(e.target.value)}><option value="">Any</option><option value="new">New</option><option value="renewal">Renewal</option></select>}</Field>
        <div className={s.grid2}>
          <Field label="Minimum sale" hint="Optional, before GST">{(a) => <MoneyInput {...a} value={minAmt} onChange={setMinAmt} />}</Field>
          <Field label="Cap per event" hint="Optional">{(a) => <MoneyInput {...a} value={eventCap} onChange={setEventCap} />}</Field>
        </div>
        <Field label="Monthly cap" error={errs.caps} hint="Optional">{(a) => <MoneyInput {...a} value={monthCap} onChange={setMonthCap} />}</Field>
        <Field label="Effective from" error={errs.from}>{(a) => <input {...a} className={s.input} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
        <label className={s.field}><span><input type="checkbox" checked={stackable} onChange={(e) => setStackable(e.target.checked)} /> Stacks with other rules</span></label>
        {err ? <p className={s.formErr} role="alert">{err}</p> : null}
      </div>
    </Drawer>
  );
}

/* ---------------------------------------------------------------- ledger */

function LedgerTab() {
  const sc = useScope();
  const { client, session } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const profiles = useProfiles(sc);
  const names = nameMap(profiles.data?.profiles);
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState("");
  const [staffId, setStaffId] = useState("");
  const [limit, setLimit] = useState(LEDGER_PAGE);
  const [err, setErr] = useState<string | null>(null);
  const monthOk = month === "" || isMonth(month);
  const q = useQuery({
    queryKey: ["commission", "ledger", month, status, staffId, limit, ...sc.scopeKey],
    queryFn: () => fetchLedger(client, { limit, ...(monthOk && month ? { month } : {}), ...(status ? { status } : {}), ...(staffId ? { staffId } : {}) }),
    retry: false, placeholderData: (prev) => prev,
  });
  const release = useMutation({
    mutationFn: () => releaseLedger(client),
    onSuccess: async () => { toast.show("Held commission released where packs are paid"); await qc.invalidateQueries({ queryKey: ["commission"] }); },
    onError: (e) => setErr(errorText(e)),
  });
  const canExport = sc.ctx.has("exports.commission_ledger");
  const exportCsv = async () => { setErr(null); try { await downloadCsv(session, "/commission/ledger/export", { month: monthOk && month ? month : undefined }, "commission-ledger.csv"); } catch (e) { setErr(errorText(e)); } };

  const cols: Array<Column<LedgerLine>> = [
    { key: "who", header: "Staff", cell: (l) => <span className={s.truncate} title={names.get(l.staffId) ?? l.staffId}>{names.get(l.staffId) ?? l.staffId}</span> },
    { key: "m", header: "Month", cell: (l) => l.periodMonth },
    { key: "basis", header: "Basis", cell: (l) => <span className={s.truncate} title={l.basis}>{l.basis}</span> },
    { key: "rule", header: "Rule", cell: (l) => `v${l.ruleVersion}` },
    { key: "st", header: "Status", cell: (l) => <Badge tone={l.status === "held" ? "warn" : l.status === "reversed" ? "crit" : l.status === "released" ? "ok" : "info"}>{l.status}</Badge> },
    { key: "amt", header: "Amount", align: "end", cell: (l) => <b className={s.num}>{sc.money(l.amount)}</b> },
  ];
  return (
    <Card aria-label="Commission ledger">
      <CardHeader title="Ledger" subtitle="Every commission line, with the rule version that produced it." action={<div className={s.actions}>
        {sc.ctx.has("commission.release") ? <Button size="sm" busy={release.isPending} busyLabel="Releasing…" onClick={() => release.mutate()}>Release held</Button> : null}
        {canExport ? <Button size="sm" onClick={() => void exportCsv()}>Export CSV</Button> : null}
      </div>} />
      <div className={s.tools}>
        <Field label="Month" error={monthOk ? null : "Use YYYY-MM."}>{(a) => <input {...a} className={s.input} value={month} onChange={(e) => { setMonth(e.target.value); setLimit(LEDGER_PAGE); }} placeholder="YYYY-MM" />}</Field>
        <Field label="Status">{(a) => <select {...a} className={s.input} value={status} onChange={(e) => { setStatus(e.target.value); setLimit(LEDGER_PAGE); }}><option value="">All</option>{["approved", "held", "released", "reversed"].map((x) => <option key={x} value={x}>{x}</option>)}</select>}</Field>
        <Field label="Staff">{(a) => <select {...a} className={s.input} value={staffId} onChange={(e) => { setStaffId(e.target.value); setLimit(LEDGER_PAGE); }}><option value="">Everyone</option>{[...names].map(([id, n]) => <option key={id} value={id}>{n}</option>)}</select>}</Field>
      </div>
      {err ? <p className={s.formErr} role="alert">{err}</p> : null}
      <Gate query={q} what="the ledger">
        {(d) => d.lines.length === 0 ? <Empty icon="cash" title="No commission yet" description="Lines appear here as sales and PT sessions earn commission." /> : (
          <Table label="Commission ledger lines" columns={cols} rows={d.lines} rowKey={(l) => l.id} minWidth={640} summary={`${d.lines.length} lines`} hasMore={d.lines.length >= limit && limit < LEDGER_MAX} onLoadMore={() => setLimit((n) => Math.min(LEDGER_MAX, n + LEDGER_PAGE))} loadingMore={q.isFetching} />
        )}
      </Gate>
    </Card>
  );
}
